import type { ItemType } from 'cadenza/repository/model/item-type.gen';
import { type NavigatorTreeNodeDto } from 'cadenza/repository/model/navigator-tree-node-dto';
import { isParentNode } from 'cadenza/repository/model/navigator-tree-node-dto';
import { type RepositoryItemId, repositoryItemIdEquals } from 'cadenza/repository/model/repository-item-id';

export function findNode(node: NavigatorTreeNodeDto, itemId: RepositoryItemId): NavigatorTreeNodeDto | undefined;
export function findNode(
  node: NavigatorTreeNodeDto,
  predicate: (node: NavigatorTreeNodeDto) => boolean,
): NavigatorTreeNodeDto | undefined;
export function findNode(
  node: NavigatorTreeNodeDto,
  itemIdOrPredicate: RepositoryItemId | ((node: NavigatorTreeNodeDto) => boolean),
): NavigatorTreeNodeDto | undefined {
  if (typeof itemIdOrPredicate === 'function') {
    const predicate = itemIdOrPredicate as (node: NavigatorTreeNodeDto) => boolean;
    return findNodeImpl(node, predicate);
  } else {
    const itemId = itemIdOrPredicate as RepositoryItemId;
    return findNodeImpl(node, (node) => !!node.itemId && repositoryItemIdEquals(itemId, node.itemId));
  }
}

function findNodeImpl(
  node: NavigatorTreeNodeDto,
  predicate: (node: NavigatorTreeNodeDto) => boolean,
): NavigatorTreeNodeDto | undefined {
  if (predicate(node)) {
    return node;
  }
  if (isParentNode(node)) {
    for (const child of node.children) {
      const foundNode = findNodeImpl(child, predicate);
      if (foundNode) {
        return foundNode;
      }
    }
  }
}

/**
 * Filters the given navigator tree with the given {@link NavigatorTreeNodeDto} predicate.
 *
 * - Filters the tree depth first.
 * - Keeps nodes that are accepted by the predicate and their ancestors. The root node is always kept.
 */
export function filterNavigatorTree(
  node: NavigatorTreeNodeDto,
  predicate: (node: NavigatorTreeNodeDto) => boolean,
): NavigatorTreeNodeDto;
/**
 * This overload accepts a set of item types, from which a {@link NavigatorTreeNodeDto} predicate is created.
 */
export function filterNavigatorTree(node: NavigatorTreeNodeDto, types: ItemType[]): NavigatorTreeNodeDto;
export function filterNavigatorTree(
  node: NavigatorTreeNodeDto,
  predicateOrTypes: ((node: NavigatorTreeNodeDto) => boolean) | ItemType[],
) {
  if (!isParentNode(node)) {
    return node;
  }
  const predicate = Array.isArray(predicateOrTypes) ? byItemType(predicateOrTypes) : predicateOrTypes;
  const result: Omit<typeof node, 'children'> & Partial<Pick<typeof node, 'children'>> = { ...node };
  result.children = node.children
    .map((child) => ({ ...filterNavigatorTree(child, predicate), parent: result }) as NavigatorTreeNodeDto)
    .filter((child) => (isParentNode(child) && child.children.length > 0) || predicate(child));
  return result;
}

/**
 * Returns a {@link NavigatorTreeNodeDto} predicate: A node is accepted if its `itemType` is in the given set of item types.
 */
export function byItemType(types: ItemType[]) {
  const typesSet = new Set(types);
  return (node: NavigatorTreeNodeDto) => typesSet.has(node.itemType);
}
